/**
 * Transaction Classifier
 *
 * Pure function that inspects on-chain transaction data (token balance changes
 * and involved program IDs) to classify transactions into portfolio categories.
 */

import { LEGACY_USDV_MINT, USDV_MINT } from "@shared/tokens/usdv";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { STAKE_PROGRAM_ID_STRING, VAULT_PROGRAM_ID_STRING, WELL_KNOWN_TOKENS_STRINGS } from "@solomon-labs/constants";
import type { TransactionMetaTokenBalance, TransactionResponse } from "@solomon-labs/solana-rpc-client";

import type { ClassifiedTransaction, PortfolioTransactionType } from "./types";

// Reverse lookup: mint address → symbol
const MINT_TO_SYMBOL: Record<string, string> = {
    [WELL_KNOWN_TOKENS_STRINGS.USDC]: "USDC",
    [WELL_KNOWN_TOKENS_STRINGS.USDT]: "USDT",
    [WELL_KNOWN_TOKENS_STRINGS.PYUSD]: "PYUSD"
};

interface BalanceDiff {
    mint: string;
    diff: BigDecimal;
    decimals: number;
}

/**
 * Compute token balance diffs for a specific wallet owner
 */
function computeBalanceDiffs(
    preBalances: TransactionMetaTokenBalance[],
    postBalances: TransactionMetaTokenBalance[],
    walletAddress: string
): BalanceDiff[] {
    const preByMint = sumBalancesByMint(preBalances, walletAddress);
    const postByMint = sumBalancesByMint(postBalances, walletAddress);
    const mints = new Set([...preByMint.keys(), ...postByMint.keys()]);
    const diffs: BalanceDiff[] = [];

    for (const mint of mints) {
        const pre = preByMint.get(mint);
        const post = postByMint.get(mint);
        const diff = (post?.amount ?? new BigDecimal(0)).subtract(pre?.amount ?? new BigDecimal(0));

        if (!diff.eq(0)) {
            diffs.push({
                mint,
                diff,
                decimals: post?.decimals ?? pre?.decimals ?? 0
            });
        }
    }

    return diffs;
}

interface AggregatedTokenBalance {
    amount: BigDecimal;
    decimals: number;
}

function sumBalancesByMint(
    balances: TransactionMetaTokenBalance[],
    walletAddress: string
): Map<string, AggregatedTokenBalance> {
    const totals = new Map<string, AggregatedTokenBalance>();

    for (const balance of balances) {
        if (balance.owner !== walletAddress) continue;

        const amount = new BigDecimal(balance.uiTokenAmount.amount);
        const current = totals.get(balance.mint);
        totals.set(balance.mint, {
            amount: current ? current.amount.add(amount) : amount,
            decimals: balance.uiTokenAmount.decimals
        });
    }

    return totals;
}

/**
 * Extract all account keys from a transaction (handles legacy, versioned, and parsed formats)
 */
function getAllAccountKeys(tx: TransactionResponse): string[] {
    const message = tx.transaction.message;
    const keys: string[] = [];

    for (const key of message.accountKeys) {
        if (typeof key === "string") {
            keys.push(key);
        } else if ("pubkey" in key) {
            keys.push(key.pubkey);
        }
    }

    // Include loaded addresses from versioned transactions
    if (tx.meta?.loadedAddresses) {
        keys.push(...tx.meta.loadedAddresses.readonly);
        keys.push(...tx.meta.loadedAddresses.writable);
    }

    return keys;
}

/**
 * Classify a single on-chain transaction into a portfolio activity type
 */
export function classifyTransaction(tx: TransactionResponse, walletAddress: string): ClassifiedTransaction {
    const signature = tx.transaction.signatures[0];
    const timestamp = tx.blockTime ?? 0;

    const result: ClassifiedTransaction = {
        signature,
        type: "unknown",
        timestamp,
        usdvAmount: null,
        legacyUsdvAmount: null,
        susdvAmount: null,
        otherToken: null
    };

    if (!tx.meta) return result;

    // Compute balance diffs
    const diffs = computeBalanceDiffs(tx.meta.preTokenBalances ?? [], tx.meta.postTokenBalances ?? [], walletAddress);

    const usdvDiff = diffs.find((d) => d.mint === USDV_MINT);
    const legacyUsdvDiff = diffs.find((d) => d.mint === LEGACY_USDV_MINT);
    const susdvDiff = diffs.find((d) => d.mint === WELL_KNOWN_TOKENS_STRINGS.sUSDv);

    // A legacy balance is relevant only when it is exchanged for current
    // USDv. Old transfers must not appear as current USDv buys or sells.
    const isUpgrade =
        legacyUsdvDiff !== undefined && usdvDiff !== undefined && legacyUsdvDiff.diff.lt(0) && usdvDiff.diff.gt(0);

    // No current USDv/sUSDv changes — or a legacy-only change — is not a
    // normal portfolio transaction.
    if ((!usdvDiff && !susdvDiff) || (legacyUsdvDiff && !isUpgrade)) {
        return result;
    }

    // Convert raw amounts to human-readable
    if (usdvDiff) {
        result.usdvAmount = toUiAmount(usdvDiff);
    }
    if (isUpgrade && legacyUsdvDiff) {
        result.legacyUsdvAmount = toUiAmount(legacyUsdvDiff);
    }
    if (susdvDiff) {
        result.susdvAmount = toUiAmount(susdvDiff);
    }

    // Extract collateral token change (USDC, USDT, etc.)
    const collateralDiff = diffs.find((d) => MINT_TO_SYMBOL[d.mint]);
    if (collateralDiff) {
        result.otherToken = {
            symbol: MINT_TO_SYMBOL[collateralDiff.mint],
            amount: toUiAmount(collateralDiff)
        };
    }

    // Determine which program was involved
    const accountKeys = getAllAccountKeys(tx);
    const hasVaultProgram = accountKeys.includes(VAULT_PROGRAM_ID_STRING as string);
    const hasStakeProgram = accountKeys.includes(STAKE_PROGRAM_ID_STRING as string);

    result.type = isUpgrade
        ? "upgrade"
        : classifyType(
              hasVaultProgram,
              hasStakeProgram,
              usdvDiff?.diff ?? new BigDecimal(0),
              susdvDiff?.diff ?? new BigDecimal(0)
          );

    return result;
}

/**
 * Determine transaction type from program involvement and balance directions
 */
function classifyType(
    hasVaultProgram: boolean,
    hasStakeProgram: boolean,
    usdvDiff: BigDecimal,
    susdvDiff: BigDecimal
): PortfolioTransactionType {
    if (hasVaultProgram) {
        // Vault program: Mint or Redeem
        if (usdvDiff.gt(0)) return "mint";
        if (usdvDiff.lt(0)) return "redeem";
    }

    if (hasStakeProgram) {
        // Stake program: Stake, Unstake, or Withdraw
        if (susdvDiff.gt(0) && usdvDiff.lt(0)) return "stake";
        if (susdvDiff.lt(0)) return "unstake";
        if (usdvDiff.gt(0) && susdvDiff.eq(0)) return "withdraw";
    }

    // No matching program — Jupiter swap or direct transfer
    if (usdvDiff.gt(0)) return "buy";
    if (usdvDiff.lt(0)) return "sell";

    return "unknown";
}

function toUiAmount(balanceDiff: BalanceDiff): BigDecimal {
    return balanceDiff.diff.abs().divide(BigDecimal.pow10(balanceDiff.decimals));
}
